import _ from 'underscore';

function mergePlacementList(prevPlacementList, curPlacementList) {
  if (!prevPlacementList && !curPlacementList) {
    return [false, prevPlacementList];
  }
  if (!prevPlacementList || !curPlacementList) {
    return [true, curPlacementList];
  }

  let changed = prevPlacementList.length !== curPlacementList.length;
  const newPlacementList = [];
  for (let i = 0; i < curPlacementList.length; i += 1) {
    const curPlacement = curPlacementList[i];
    if (curPlacement) {
      const prevPlacement = _.find(prevPlacementList, ({ placementMeta }) => placementMeta.placementType === curPlacement.placementMeta.placementType);
      if (prevPlacement && _.isEqual(prevPlacement, curPlacement)) {
        // find same placement
        newPlacementList.push(prevPlacement);
      } else {
        newPlacementList.push(curPlacement);
        changed = true;
      }
    }
  }

  return [
    changed,
    changed ? newPlacementList : prevPlacementList,
  ];
}

function mergePlacementSets(prevPlacementSets, curPlacementSets) {
  if (!prevPlacementSets && !curPlacementSets) {
    return [false, prevPlacementSets];
  }
  if (!prevPlacementSets || !curPlacementSets) {
    return [true, curPlacementSets];
  }

  let changed = prevPlacementSets.length !== curPlacementSets.length;
  const newPlacementSets = [];
  for (let i = 0; i < curPlacementSets.length; i += 1) {
    const curPlacementSet = curPlacementSets[i];
    const prevPlacementSet = _.find(prevPlacementSets, ({ name }) => name === curPlacementSet.name);
    if (prevPlacementSet) {
      const [isListChanged, newPlacementList] = mergePlacementList(prevPlacementSet.placementList, curPlacementSet.placementList);
      newPlacementSets.push(isListChanged ? { ...curPlacementSet, placementList: newPlacementList } : prevPlacementSet);
      changed = changed || isListChanged;
    } else {
      newPlacementSets.push(curPlacementSet);
      changed = true;
    }
  }

  return [changed, changed ? newPlacementSets : prevPlacementSets];
}

function mergePreviewImageAndPlacementSets(prevImageAndPlacementSets, curImageAndPlacementSets) {
  if (!prevImageAndPlacementSets && !curImageAndPlacementSets) {
    return [false, prevImageAndPlacementSets];
  }
  if (!prevImageAndPlacementSets || !curImageAndPlacementSets) {
    return [true, curImageAndPlacementSets];
  }
  if (prevImageAndPlacementSets === curImageAndPlacementSets) {
    return [false, prevImageAndPlacementSets];
  }

  let changed = prevImageAndPlacementSets.length !== curImageAndPlacementSets.length;
  const prevImageAndPlacementSetsCopy = [...prevImageAndPlacementSets];
  const newImageAndPlacementSets = [];
  for (let i = 0; i < curImageAndPlacementSets.length; i += 1) {
    const curImageAndPlacementSet = curImageAndPlacementSets[i];
    const idx = _.findIndex(prevImageAndPlacementSetsCopy, ({ previewImage }) =>
      previewImage.imageMeta && curImageAndPlacementSet.previewImage.imageMeta
      && (previewImage.imageMeta.url === curImageAndPlacementSet.previewImage.imageMeta.url));
    if (idx >= 0) {
      const prevImageAndPlacementSet = prevImageAndPlacementSetsCopy[idx];
      const { previewImage: prevPreviewImage, placementSets: prevPlacementSets } = prevImageAndPlacementSet;
      const { previewImage: curPreviewImage, placementSets: curPlacementSets } = curImageAndPlacementSet;

      const isPreviewImageChanged = !_.isEqual(prevPreviewImage, curPreviewImage);
      const [isPlacementSetsChanged, newPlacementSets] = mergePlacementSets(prevPlacementSets, curPlacementSets);
      newImageAndPlacementSets.push(isPreviewImageChanged || isPlacementSetsChanged ? { previewImage: curPreviewImage, placementSets: newPlacementSets } : prevImageAndPlacementSet);
      changed = changed || isPlacementSetsChanged || isPreviewImageChanged;

      // remove the matched item
      prevImageAndPlacementSetsCopy.splice(idx, 1);
    } else {
      newImageAndPlacementSets.push(curImageAndPlacementSet);
      changed = true;
    }
  }

  // there are some remaining items in prevImageAndPlacementSetsCopy, which are not in curImageAndPlacementSets
  changed = changed || prevImageAndPlacementSetsCopy.length > 0;

  return [changed, changed ? newImageAndPlacementSets : prevImageAndPlacementSets];
}

// merge reducer states, try to keep previous sub states if values are not changed
export function mergeReducerStates(prevState, curState) {
  if (!prevState && !curState) {
    return [false, prevState];
  }

  if (!prevState || !curState) {
    return [true, curState];
  }

  const {
    previewImageListState: prevPreviewState,
    hasDataChanged: prevHasDatachanged,
    activeImageIndices: prevActiveImageIndices,
  } = prevState;

  const {
    previewImageListState: curPreviewState,
    hasDataChanged: curHasDatachanged,
    activeImageIndices: curActiveImageIndices,
  } = curState;

  const [isPreviewStateChanged, newPreviewState] = mergePreviewImageAndPlacementSets(prevPreviewState, curPreviewState);
  const isActiveImageIndicesChanged = !_.isEqual(prevActiveImageIndices, curActiveImageIndices);
  const changed = prevHasDatachanged !== curHasDatachanged || isActiveImageIndicesChanged || isPreviewStateChanged;

  return [
    changed,
    changed ? {
      previewImageListState: isPreviewStateChanged ? newPreviewState : prevPreviewState,
      hasDataChanged: curHasDatachanged,
      activeImageIndices: isActiveImageIndicesChanged ? curActiveImageIndices : prevActiveImageIndices,
    } : prevState,
  ];
}
